import { ensureOk } from "@/api/apiError";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/api/httpClient";
import type { AccessRequestRead } from "@/types";

async function fetchAccessRequests(
  status?: string,
): Promise<AccessRequestRead[]> {
  const suffix = status ? `?status=${encodeURIComponent(status)}` : "";
  const response = await httpClient(`/access-requests${suffix}`);
  await ensureOk(response);
  return response.json() as Promise<AccessRequestRead[]>;
}

/** US-09: validator inbox — lists access requests within the caller's validator scope
 * (empty array if the caller isn't a validator for any agent). */
export function useAccessRequests(status?: string) {
  return useQuery({
    queryKey: ["access-requests", status ?? "all"],
    queryFn: () => fetchAccessRequests(status),
    staleTime: 30 * 1000,
  });
}

async function reviewAccessRequest(input: {
  id: string;
  status: "APPROVED" | "REFUSED";
}): Promise<AccessRequestRead> {
  const response = await httpClient(`/access-requests/${input.id}`, {
    method: "PATCH",
    body: JSON.stringify({ status: input.status }),
  });
  await ensureOk(response);
  return response.json() as Promise<AccessRequestRead>;
}

/** Approve/reject a pending request; approval grants the `agent-{slug}-users` group
 * (server-side side effect, US-09). */
export function useReviewAccessRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reviewAccessRequest,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["access-requests"] });
    },
  });
}
